import React, { useState, useEffect, useRef } from 'react';
import { getRecords } from '../utils/storage';
import { loadAudio, isAudioSaved } from '../utils/audioStorage';
import { getLogs, migrateLogs } from '../services/readingLogsApi';
import { getCloudAudioIds, getAudioSignedUrl, uploadAudio } from '../services/audioStorageApi';
import BottomNav from './BottomNav';
import { trackEvent } from '../utils/analytics';

const WEEK = ['日', '月', '火', '水', '木', '金', '土'];

function formatDate(localDate) {
  // localDate は "YYYY-MM-DD"
  const [y, m, d] = localDate.split('-').map(Number);
  const dateObj = new Date(y, m - 1, d);
  const w = WEEK[dateObj.getDay()];
  return `${m}月${d}日（${w}）`;
}

function starStr(n) {
  const s = Math.min(Math.max(n ?? 0, 0), 3);
  return '⭐'.repeat(s) + '☆'.repeat(3 - s);
}

export default function RecordScreen({ onNav, isPremium, childId, isGuest, currentChild, onMigrateComplete }) {
  const [records, setRecords]       = useState([]);
  const [playingId, setPlayingId]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [cloudIds, setCloudIds]     = useState([]); // クラウド保存済みIDセット
  const [detailRec, setDetailRec]   = useState(null); // 詳細モーダル表示中のレコード
  const [retryingId, setRetryingId] = useState(null); // クラウド保存を再試行中のID
  const [toastMsg, setToastMsg]     = useState(null);  // 'success' | 'notfound' | 'error' | null
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      if (!isGuest && childId) {
        // ログイン済み：Supabaseから記録取得
        const logs = await getLogs(childId);
        const cloudLogs = logs || [];
        if (cloudLogs.length === 0) {
          // Supabaseに記録がない場合はlocalStorageにフォールバック
          // （アカウント作成前・移行前の記録を表示するため）
          // ⚠️ strict:true で厳密にchildId一致のみ。ここはクラウドへの自動移行を伴うため、
          //    未タグ（誰のものか不明）の記録まで含めると、端末を共有するきょうだいの記録が
          //    混ざってこの子のクラウド記録として移行されてしまう（2026-08-19修正）
          const localLogs = getRecords(childId, { strict: true }).slice().reverse();
          if (!cancelled) setRecords(localLogs);
          // localStorageに記録があればSupabaseへ自動移行（カレンダーも正しく反映される）
          if (localLogs.length > 0) {
            await migrateLogs(localLogs, childId);
            // 移行後にSupabaseから再取得して表示を更新
            const migratedLogs = await getLogs(childId);
            if (!cancelled) setRecords(migratedLogs || localLogs);
            if (onMigrateComplete) onMigrateComplete(); // ホーム画面のカレンダー統計を更新
          }
        } else {
          if (!cancelled) setRecords(cloudLogs);
        }
        // ログイン済みならクラウド音声IDも取得（無料3件・プレミアム20件）
        if (childId) {
          const ids = await getCloudAudioIds(childId);
          if (!cancelled) setCloudIds(ids);
        }
      } else {
        // ゲスト：localStorageから取得
        const all = getRecords(childId).slice().reverse();
        if (!cancelled) setRecords(all);
        // ゲストのきろく件数を計測（ログイン誘導バナーの効果測定用）
        if (!cancelled && all.length >= 10) {
          trackEvent('ondoku_guest_record_count', { count: all.length });
        }
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
      audioRef.current?.pause();
    };
  }, [isGuest, childId, isPremium, onMigrateComplete]);

  const handlePlay = async (rec) => {
    // 再生中のものをタップ → 停止
    if (playingId === rec.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause();

    let url = null;
    let isObjectUrl = false;

    // ① ローカル（IndexedDB）を優先
    const blob = await loadAudio(rec.id);
    if (blob) {
      url = URL.createObjectURL(blob);
      isObjectUrl = true;
    }
    // ② なければクラウド（ログイン済みのみ、無料3件・プレミアム20件）
    if (!url && childId && cloudIds.includes(rec.id)) {
      url = await getAudioSignedUrl(rec.id, childId);
    }

    if (!url) {
      alert('録音データが　みつかりません。');
      return;
    }

    const audio = new Audio(url);
    audioRef.current = audio;
    audio.play();
    audio.onended = () => {
      setPlayingId(null);
      if (isObjectUrl) URL.revokeObjectURL(url);
    };
    setPlayingId(rec.id);
  };

  /**
   * クラウド保存の再試行。
   * 端末（IndexedDB）に音声が残っているのにクラウドには無い状態（アップロードが
   * 投げっぱなしで失敗していた場合など）だけ表示・実行できる。
   * 端末にも無い場合はどこにも音声が残っていないので、これでは復元できない。
   */
  const retryUpload = async (rec) => {
    if (!childId) return;
    setRetryingId(rec.id);
    const blob = await loadAudio(rec.id);
    if (!blob) {
      setToastMsg('notfound');
      setRetryingId(null);
      return;
    }
    const ok = await uploadAudio(rec.id, blob, childId, blob.type, isPremium);
    if (ok) {
      setCloudIds((prev) => (prev.includes(rec.id) ? prev : [...prev, rec.id]));
      setToastMsg('success');
    } else {
      setToastMsg('error');
    }
    setRetryingId(null);
  };

  // トーストは数秒で自動的に消す
  useEffect(() => {
    if (!toastMsg) return;
    const t = setTimeout(() => setToastMsg(null), 3500);
    return () => clearTimeout(t);
  }, [toastMsg]);

  // 日付でグループ化
  const grouped = {};
  records.forEach((r) => {
    const d = r.localDate || '不明';
    if (!grouped[d]) grouped[d] = [];
    grouped[d].push(r);
  });
  const dates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div className="screen record-screen">

      {/* ─── ヘッダー ─── */}
      <div className="v9-header" style={{ width: '100%', flexShrink: 0 }}>
        <div className="v9-blob1" />
        <div className="v9-blob2" />
        <div className="v9-header-inner">
          <div className="v9-header-top">
            <div>
              <div className="v9-logo-sub">READING APP</div>
              <div className="v9-logo-main">Ondoku <span>Quest</span></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {currentChild && (
                <span style={{ color: 'white', fontSize: 16, fontWeight: 700, opacity: 0.9 }}>
                  {currentChild.avatar} {currentChild.name}
                </span>
              )}
              <button
                onClick={() => onNav('home')}
                style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 24, color: 'white', fontSize: 13, fontWeight: 700, padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit' }}
              >← ホーム</button>
            </div>
          </div>
          <div className="v9-greeting-msg" style={{ marginBottom: 0 }}>
            よんだ　<span>きろく</span>
          </div>
        </div>
      </div>

      {/* ─── きろく一覧 ─── */}
      <div className="record-list">

        {/* ゲスト：クラウド同期の案内 */}
        {isGuest && (
          <div className="record-limit-banner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span>📱 ゲストのきろくはこの端末だけに保存されています。ログインすると他の端末でも見られます</span>
            <button
              onClick={() => onNav('auth')}
              style={{ flexShrink: 0, background: 'var(--purple)', color: 'white', border: 'none', borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 700, fontFamily: 'var(--font)', cursor: 'pointer' }}
            >無料登録</button>
          </div>
        )}

        {/* 無料ログイン：クラウド保存の説明 */}
        {!isGuest && !isPremium && (
          <div className="record-limit-banner">
            ☁️ きろくはクラウドに保存されます（音声は最新3件）
          </div>
        )}

        {/* プレミアム：クラウド保存の説明 */}
        {!isGuest && isPremium && (
          <div className="record-limit-banner record-limit-banner--premium">
            👑 きろくはクラウドに保存されます（音声は最新50件）
          </div>
        )}

        {loading ? (
          <div className="record-empty">
            <div className="record-empty-icon">⏳</div>
            <p className="record-empty-text">よみこみちゅう…</p>
          </div>
        ) : dates.length === 0 ? (
          <div className="record-empty">
            <div className="record-empty-icon">📖</div>
            <p className="record-empty-text">まだ　きろくが　ありません</p>
            <p className="record-empty-sub">おんどくして　きろくをつけよう！</p>
          </div>
        ) : (
          dates.map((date) => (
            <div key={date}>
              {/* 日付ヘッダー */}
              <div className="record-date-header">
                {formatDate(date)}
              </div>

              {/* その日のレコード */}
              {grouped[date].map((rec, i) => (
                <div key={rec.id || i} className="record-item">
                  <div className="record-item-body">
                    <div className="record-item-title">『{rec.title}』</div>
                    <div className="record-item-meta">
                      <span className={`record-mode-badge ${rec.mode === 'honban' ? 'mode-honban' : 'mode-renshyu'}`}>
                        {rec.mode === 'honban' ? 'ほんばん' : 'れんしゅう'}
                      </span>
                      <span className="record-stars">{starStr(rec.totalStars)}</span>
                      <span className="record-score">{rec.avgScore ?? '—'}てん</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {/* 詳細ボタン（ログイン済みでtranscript・aiFeedback・aiDetailsのいずれかがある場合のみ） */}
                    {!isGuest && rec.mode === 'honban' && (rec.transcript || rec.aiFeedback || rec.aiDetails) && (
                      <button
                        className="record-detail-btn"
                        onClick={() => setDetailRec(rec)}
                        title="採点の詳細をみる"
                      >
                        詳細
                      </button>
                    )}
                    {/* ローカルまたはクラウドに音声がある場合のみ▶ボタンを表示 */}
                    {rec.mode === 'honban' && (isAudioSaved(rec.id) || cloudIds.includes(rec.id)) && (
                      <button
                        className={`record-play-btn ${playingId === rec.id ? 'playing' : ''}`}
                        onClick={() => handlePlay(rec)}
                        title={cloudIds.includes(rec.id) && !isAudioSaved(rec.id) ? '☁️ クラウドから再生' : '録音をきく'}
                      >
                        {playingId === rec.id ? '⏹' : (cloudIds.includes(rec.id) && !isAudioSaved(rec.id) ? '☁️' : '▶')}
                      </button>
                    )}
                    {/* 端末には音声があるがクラウドにまだ無い（アップロード失敗など）→ 再試行ボタン */}
                    {!isGuest && rec.mode === 'honban' && isAudioSaved(rec.id) && !cloudIds.includes(rec.id) && (
                      <button
                        className="record-cloud-retry-btn"
                        onClick={() => retryUpload(rec)}
                        disabled={retryingId === rec.id}
                        title="クラウドにまだ保存されていません。押すと保存し直します"
                      >
                        {retryingId === rec.id ? '⏳' : '☁️+'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* ─── 詳細モーダル ─── */}
      {detailRec && (
        <div className="record-detail-overlay" onClick={() => setDetailRec(null)}>
          <div className="record-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="record-detail-header">
              <div className="record-detail-title">『{detailRec.title}』</div>
              <div className="record-detail-meta">
                <span className="record-detail-score">{detailRec.avgScore ?? '—'}てん</span>
                <span className="record-detail-stars">{starStr(detailRec.totalStars)}</span>
              </div>
            </div>

            {detailRec.aiFeedback && (
              <div className="record-detail-section">
                <div className="record-detail-section-label">🤖 AIからひとこと</div>
                <div className="record-detail-feedback">{detailRec.aiFeedback}</div>
              </div>
            )}

            {detailRec.aiDetails && (
              <div className="record-detail-section">
                <div className="record-detail-section-label">📝 くわしいコメント</div>
                <div className="record-detail-comment">{detailRec.aiDetails}</div>
              </div>
            )}

            {detailRec.transcript && (
              <div className="record-detail-section">
                <div className="record-detail-section-label">🎤 よみとった文</div>
                <div className="record-detail-transcript">{detailRec.transcript}</div>
              </div>
            )}

            <button className="record-detail-close" onClick={() => setDetailRec(null)}>
              とじる
            </button>
          </div>
        </div>
      )}

      <BottomNav active="record" onNav={onNav} />

      {/* クラウド保存の再試行結果トースト */}
      {toastMsg === 'success' && (
        <div className="payment-toast payment-toast-success" onClick={() => setToastMsg(null)}>
          ☁️ クラウドに保存しました！
        </div>
      )}
      {toastMsg === 'error' && (
        <div className="payment-toast payment-toast-cancel" onClick={() => setToastMsg(null)}>
          保存に失敗しました。もう一度お試しください
        </div>
      )}
      {toastMsg === 'notfound' && (
        <div className="payment-toast payment-toast-cancel" onClick={() => setToastMsg(null)}>
          この録音は端末にも残っていません
        </div>
      )}
    </div>
  );
}
